// Need to handle save and continue button is case preview is hidden

// ** React Imports
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

// ** Type Imports
import {
  DiscountFormStateType,
  DiscountFlowConfigType,
  JourneyStepType,
  JourneyLineStepConfigType,
  DiscountFormErrorState,
  FeatherIconType,
  JourneyLineActionType,
  PaymentModeConcessionConfigType
} from 'src/lib/types/discounts'
import { DiscountFLowTabs } from 'src/lib/types/activityLogTypes'

// ** Util Imports
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useRouter } from 'next/router'
import { produce } from 'immer'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Child imports
import DiscountJourneyLine from './DiscountJourneyLine'
import ProgressiveDiscountSummary from './ProgressiveDiscountSummary'
import DiscountCreationForm from './DiscountCreationForm'
import { Button, Skeleton } from '@mui/material'
import { getDiscountById, editDiscountById, createDiscount, setMerchantAttributes } from 'src/lib/core/api-methods'
import { alertSeverites, discountTableConstants } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { parseDiscountConfig } from 'src/lib/utils/parsers'
import {
  createDiscountPayload,
  getDefaultHeaderForPaymentOffer,
  getDiscountTypeForGTM,
  getPaymentModeConcessions
} from 'src/lib/utils/discountHelpers'
import { createButton, defaultErrorState, initialFormErrorState } from 'src/assets/DiscountsConfig'
import { isInvalidForm } from 'src/lib/utils/validations/discountsValidations'
import { LoadingButton } from '@mui/lab'
import * as Icon from 'react-feather'
import { prepaidPaymentMode } from '../../../assets/DiscountsConfig'
import DiscountsChannelsForm from './DiscountChannelsContext'
import { UIPreviewPaymentMethodType } from 'src/lib/types/uiPreview'
import { useUser } from 'src/lib/contexts/UserContext'

import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'
import { ExperimentCreatedDiscountType } from 'src/lib/types/experiments'
import { CopiedDiscountInfo, DiscountInfo } from 'src/lib/types/common/discounts'
import PaymentPagePreview from 'src/components/ui-customization/sub-components/payment-page/PaymentPagePreview'
import useGTM from 'src/lib/hooks/useGTM'
import { PaymentPageProvider } from 'src/lib/contexts/PaymentPageContext'

interface DiscountCreationFlowProps {
  flowConfig: DiscountFlowConfigType | undefined
  discountId: string | string[] | undefined
  setCreatedDiscount?: (discountData: ExperimentCreatedDiscountType) => void
  handleClose?: () => void
  copiedDiscountInfo?: CopiedDiscountInfo
  context?: 'experiment' | 'none'
}

const DiscountCreationFlow: React.FC<DiscountCreationFlowProps> = ({
  flowConfig,
  discountId,
  setCreatedDiscount,
  handleClose,
  copiedDiscountInfo,
  context = 'none'
}) => {
  const router = useRouter()
  const {
    getters: { merchant, paymentModes },
    actions: { setMerchantData }
  } = useMerchant()
  const { t } = useLocale()
  const { setLoaderProperties } = useLoader() // Add this line
  const { user } = useUser()
  const accountId = merchant?.accountId
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const addressConfig = merchant?.attributes?.addressConfig

  // Desctructuring the flow config passed by journey line.
  const { type, generationType, defaultDiscountState, journeyLineConfig, periodLength, applicationStage, goBackUrl } =
    flowConfig || {}

  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [selectedStep, setSelectedStep] = useState<JourneyStepType>('setup')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [discountFormState, setDiscountFormState] = useState<DiscountFormStateType | undefined>(defaultDiscountState)
  const [formErrors, setFormErrors] = useState<DiscountFormErrorState>(initialFormErrorState)
  const [isEditMode, setIsEditMode] = useState<boolean>(Boolean(flowConfig?.isEditMode))

  const [modifiedJourneyLineConfig, setModifiedJourneyLineConfig] = useState<JourneyLineStepConfigType[] | undefined>(
    journeyLineConfig
  )

  const currentJourneyStepConfig = modifiedJourneyLineConfig?.find(
    (step: JourneyLineStepConfigType) => step?.step === selectedStep
  )
  const nextStepIndex =
    (modifiedJourneyLineConfig?.findIndex((step: JourneyLineStepConfigType) => step?.step === selectedStep) ?? 0) + 1
  const lastStepIndex = modifiedJourneyLineConfig?.findLastIndex(
    (step: JourneyLineStepConfigType) => !Boolean(step?.isHidden)
  )

  const [saveTriggered, setSaveTriggered] = useState<boolean>(false)

  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()

  useEffect(() => {
    if (merchant && Boolean(isEditMode) && typeof discountId === 'string') {
      // Fetch discount data using discount id passed on to the query params.
      createDiscountObject(discountId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant])

  useEffect(() => {
    if (typeof merchant !== 'undefined' && copiedDiscountInfo) {
      // Fetch discount data using discount id passed on to the query params.
      copyDiscountObject()
    }
  }, [copiedDiscountInfo])

  const goToNextStep = () => {
    if (modifiedJourneyLineConfig?.[nextStepIndex ?? 0]?.isHidden) {
      const closestNonHiddenStepIndex = modifiedJourneyLineConfig?.findIndex(
        (journeyStepConfig: JourneyLineStepConfigType, index: number) =>
          index > nextStepIndex && !Boolean(journeyStepConfig?.isHidden)
      )
      setSelectedStep(modifiedJourneyLineConfig?.[closestNonHiddenStepIndex ?? 0]?.step ?? 'setup')
      return
    }
    setSelectedStep(modifiedJourneyLineConfig?.[nextStepIndex ?? 0]?.step ?? 'setup')
  }

  const copyDiscountObject = async () => {
    if (!copiedDiscountInfo) return
    const discountObject: any = await getDiscountObjectData(copiedDiscountInfo.id)
    const parsedState = await parseDiscountConfig(discountObject?.data?.data, merchant, 'SINGLE', paymentModes)
    parsedState.setup.applicationMethodConfig.value = ''
    setDiscountFormState(parsedState)
  }

  const createDiscountObject = async (discountId?: string) => {
    if (typeof discountId === 'undefined') return
    const discountObject: any = await getDiscountObjectData(discountId)
    setDiscountFormState(await parseDiscountConfig(discountObject?.data?.data, merchant, 'SINGLE', paymentModes))
  }

  // ** Fetches discount response and returns it to the calling function ** //
  const getDiscountObjectData = async (discountId: string) => {
    try {
      setIsLoading(true)
      const response: any = await getDiscountById(issuerId, accountId, discountId)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response.error)
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        return
      }
      setIsLoading(false)
      return response
    } catch (e) {
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
    }
  }

  const updateStep = (updatedIndex: number | undefined, action: JourneyLineActionType, value: boolean) => {
    setModifiedJourneyLineConfig(
      produce((draft: any) => {
        switch (action) {
          case 'hide':
            draft[updatedIndex ?? 0].isHidden = value
            break
          case 'disable':
            draft[updatedIndex ?? 0].isDisabled = value
            break
        }
      })
    )
  }

  useEffect(() => {
    if (!Boolean(saveTriggered)) return
    handleSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountFormState, saveTriggered])

  const checkTypeForPayload = (type: string | undefined) => {
    switch (type) {
      case DiscountFLowTabs.CREATE_C2P_DISCOUNT:
        return EntityName.RTO_SUITE
      case DiscountFLowTabs.EDIT_C2P_DISCOUNT:
        return EntityName.RTO_SUITE
      case DiscountFLowTabs.CREATE_PAYMENT_OFFER:
        return EntityName.PAYMENT_OFFERS
      case DiscountFLowTabs.EDIT_PAYMENT_OFFER:
        return EntityName.PAYMENT_OFFERS
      default:
        return EntityName.DISCOUNTS
    }
  }

  useEffect(() => {
    if (discountFormState?.setup?.applicationMethodConfig?.valueType === 'AUTOMATIC') {
      const updatedIndex = modifiedJourneyLineConfig?.findIndex(
        (journeyStep: JourneyLineStepConfigType) => journeyStep.step === 'preview'
      )
      if (applicationStage === 'PRE_PAYMENT_VALIDATION') return
      updatedIndex !== -1 && updateStep(updatedIndex, 'hide', true)
    } else {
      const updatedIndex = modifiedJourneyLineConfig?.findIndex(
        (journeyStep: JourneyLineStepConfigType) => journeyStep.step === 'preview'
      )
      updatedIndex !== -1 && updateStep(updatedIndex, 'hide', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountFormState?.setup?.applicationMethodConfig?.valueType])

  const getActionName = (type: string | undefined) => {
    return checkTypeForPayload(type) === EntityName.RTO_SUITE
      ? 'edited configuration'
      : isEditMode
      ? 'edited coupon'
      : 'created coupon'
  }

  const getMetadata = (type: string | undefined, modifiedPayload: any) => {
    return checkTypeForPayload(type) === EntityName.RTO_SUITE
      ? {}
      : {
          coupon_code: discountFormState?.setup?.applicationMethodConfig?.value,
          rules: modifiedPayload,
          status: isEditMode ? 'edited coupon' : 'create'
        }
  }

  const handleSave = async () => {
    // setLoaderProperties({ isLoaderVisible: true });
    const payload = createDiscountPayload(discountFormState, merchant ? merchant : undefined, isEditMode, context)
    const bulkGenerationConfig =
      discountFormState?.setup?.applicationConfig?.discountGenerationConfig?.bulkGenerationConfig
    const isCSVUpload =
      (generationType === 'BULK' && Boolean(bulkGenerationConfig?.valueType === 'CSV_UPLOAD')) ||
      Boolean(discountFormState?.customerEligibility?.valueType === 'SPECIFIC')

    let modifiedPayload: any = { ...payload }

    if (context === 'experiment') {
      modifiedPayload = {
        ...modifiedPayload,
        is_active: false
      }
    }

    // Adding discount description in case preview was altered.
    if (selectedStep === 'preview' && applicationStage !== 'PRE_PAYMENT_VALIDATION') {
      if (typeof discountFormState?.preview?.descriptionConfig?.longDescription !== 'string') return
      modifiedPayload = {
        ...modifiedPayload,
        long_description: discountFormState?.preview?.descriptionConfig?.longDescription,
        header: discountFormState?.preview?.descriptionConfig?.descriptionTitle,
        description: discountFormState?.preview?.descriptionConfig?.description?.replaceAll('•  ', '')
      }
    }

    // Adding extra payload information is the flow was post order dicount creation/editing.
    if (applicationStage === 'POST_ORDER_PLACEMENT') {
      modifiedPayload = {
        ...modifiedPayload,
        code: 'COD2PREPAID',
        ...(!isEditMode ? { tags: [discountTableConstants.DISCOUNT_TAG_SHOPFLO] } : {}),
        is_auto_applicable: false,
        is_discoverable: true,
        is_stackable: true,
        application_stage: 'POST_ORDER_PLACEMENT',
        post_validation_payment_modes: prepaidPaymentMode,
        application_target: 'ALL',
        coupon_type:
          discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType === 'FREEBIE' ? 'BXGY' : 'AMOUNT',
        ...(discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType === 'FREEBIE' && {
          header: `Get ${payload?.entitled_product_count} item for free`
        })
      }
    }
    if (applicationStage === 'PRE_PAYMENT_VALIDATION') {
      const { payloadForPaymentModeConcessions, paymentModes } = getPaymentModeConcessions(discountFormState)
      const isFreebieSpecificActive =
        discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType === 'FREEBIE'
      modifiedPayload = {
        ...modifiedPayload,
        application_stage: 'CART_DISCOVERY',
        post_validation_payment_modes: ['ALL'],
        ...(isFreebieSpecificActive
          ? Boolean(paymentModes?.length)
            ? { allowed_payment_modes_for_freebie: paymentModes, concession_amount: 100, payment_mode_concessions: {} }
            : { allowed_payment_modes_for_freebie: [], payment_mode_concessions: {} }
          : Object.keys(payloadForPaymentModeConcessions).length
          ? { payment_mode_concessions: payloadForPaymentModeConcessions, allowed_payment_modes_for_freebie: [] }
          : { allowed_payment_modes_for_freebie: [], payment_mode_concessions: {} })
      }
    }
    try {
      setIsLoading(true)
      // Appending uploaded file in case of "BULK CSV UPLOAD" and "SPECICF CUSTOMERS".
      if (isCSVUpload) {
        if (generationType === 'BULK') {
          modifiedPayload = {
            ...modifiedPayload,
            set_discount_file_meta: {
              file_url: bulkGenerationConfig?.uploadedFilePath,
              file_name: bulkGenerationConfig?.uploadedFileFormData?.name,
              size: bulkGenerationConfig?.uploadedFileFormData?.size
            }
          }
        }
        if (discountFormState?.customerEligibility?.valueType === 'SPECIFIC') {
          modifiedPayload = {
            ...modifiedPayload,
            file_meta: {
              file_url: discountFormState?.customerEligibility?.uploadedFilePath,
              file_name: discountFormState?.customerEligibility?.uploadedFileFormData?.name,
              size: discountFormState?.customerEligibility?.uploadedFileFormData?.size
            }
          }
        }
      }
      if (isEditMode) {
        sendGTMTrigger('discount_edit_completed', 'edit', {
          discountType: getDiscountTypeForGTM(
            discountFormState?.setup?.applicationConfig?.valueType ?? 'AMOUNT',
            applicationStage ?? 'CART_DISCOVERY'
          ),
          journeyStep: selectedStep
        })
      } else {
        sendGTMTrigger('discount_creation_completed', 'create', {
          discountType: getDiscountTypeForGTM(
            discountFormState?.setup?.applicationConfig?.valueType ?? 'AMOUNT',
            applicationStage ?? 'CART_DISCOVERY'
          ),
          journeyStep: selectedStep
        })
      }

      const response: any = isEditMode
        ? await editDiscountById(issuerId, merchant?.accountId, discountId, modifiedPayload)
        : await createDiscount(issuerId, merchant?.accountId, modifiedPayload, generationType === 'BULK')
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response.error)
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        setIsLoading(false)
        setSaveTriggered(false)
        setLoaderProperties({ isLoaderVisible: false })
        return
      } else {
        if (applicationStage === 'POST_ORDER_PLACEMENT') {
          const activePeriod: string =
            discountFormState?.usageLimits?.activePeriodConfig?.activePeriod?.toString() ?? '0'
          const modifiedAttributes = {
            attributes: {
              c2p: {
                enabled: addressConfig?.c2pConfig?.isEnabled,
                expiry: 60 * parseFloat(activePeriod)
              }
            }
          }
          const merchantResponse: any = await setMerchantAttributes(modifiedAttributes, accountId ?? '')
          if (merchantResponse?.error) {
            handleAlerts('error', merchantResponse.error)
            return
          }
          setMerchantData(merchantResponse?.data)
        }
        const activityLogPayload = {
          entityName: checkTypeForPayload(type),
          actionName: getActionName(type),
          metadata: getMetadata(type, modifiedPayload)
        }
        sendActivityLog(activityLogPayload)
        if (isEditMode) {
          sendGTMTrigger('discount_edit_completed', 'edit', {
            discountType: getDiscountTypeForGTM(
              discountFormState?.setup?.applicationConfig?.valueType ?? 'AMOUNT',
              applicationStage ?? 'CART_DISCOVERY'
            ),
            journeyStep: selectedStep
          })
        } else {
          sendGTMTrigger('discount_creation_completed', 'save', {
            discountType: getDiscountTypeForGTM(
              discountFormState?.setup?.applicationConfig?.valueType ?? 'AMOUNT',
              applicationStage ?? 'CART_DISCOVERY'
            ),
            journeyStep: selectedStep
          })
        }
        handleAlerts(
          alertSeverites.SEVERITY_SUCCESS,
          t('discount_creation_status', { status: isEditMode ? 'Edited the selected' : 'Created a new' })
        )
        if (setCreatedDiscount) {
          setCreatedDiscount({
            created: {
              id: response?.data?.data?.coupon_id,
              code: response?.data?.data?.code,
              title: response?.data?.data?.title
            },
            copied: copiedDiscountInfo
          })
        }
        setTimeout(() => {
          if (handleClose) {
            handleClose()
          } else {
            router.push(goBackUrl ?? '')
          }
        }, 1000)
      }
    } catch (e) {
      console.error(e)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
      setIsLoading(false)
      setSaveTriggered(false)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const getIconComponent = (requiredIcon: any) => {
    switch (requiredIcon) {
      case '₹':
        return <>₹</>
      case '%':
        return <>%</>
      default:
        const IconComponent = Icon[requiredIcon as FeatherIconType]
        return <IconComponent className='scale-75' />
    }
  }

  const handleSetCurrentStep = (clickedStep: JourneyStepType) => {
    if (isInvalidForm(discountFormState, setFormErrors, handleAlerts, isEditMode, generationType, applicationStage))
      return
    setSelectedStep(clickedStep)
  }

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  const paymentMethodsMessages: UIPreviewPaymentMethodType[] = useMemo(() => {
    const coverageType = discountFormState?.setup?.applicationConfig?.offerValueConfig?.paymentModeCoverageType
    let discountedText = Boolean(discountFormState?.setup?.applicationConfig?.discountDescription?.discountHeader)
      ? discountFormState?.setup?.applicationConfig?.discountDescription?.discountHeader
      : getDefaultHeaderForPaymentOffer(
          discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType ?? 'PERCENTAGE'
        )

    switch (discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType) {
      case 'FLAT':
        discountedText = discountedText?.replaceAll('<value>', '200')
        break
      case 'PERCENTAGE':
        discountedText = discountedText?.replaceAll('<value>', '10')
        break
      case 'PERCENTAGE_CAPPED':
        discountedText = discountedText?.replaceAll('<value>', '200')
        break
      case 'FREEBIE':
        discountedText = discountedText?.replaceAll('<value>', '200')
        break
    }

    if (coverageType === 'ALL') {
      const previewConfig: UIPreviewPaymentMethodType[] | undefined = [
        'UPI',
        'CARD',
        'NETBANKING',
        'WALLET',
        'EMI',
        'PAY_LATER'
      ]?.map((paymentMethod: any) => {
        return {
          paymentMethod: paymentMethod,
          discountText: discountedText ? discountedText : ''
        }
      })
      return Boolean(previewConfig?.length) ? previewConfig : []
    }
    if (coverageType === 'SPECIFIC') {
      const previewConfig: UIPreviewPaymentMethodType[] | undefined =
        discountFormState?.setup?.applicationConfig?.offerValueConfig?.paymentModeConcessions
          ?.filter(
            (paymentMethod: PaymentModeConcessionConfigType) =>
              ['UPI', 'CARD', 'NETBANKING', 'WALLET', 'EMI', 'PAY_LATER'].some(
                type => type === paymentMethod?.valueType
              ) && paymentMethod?.paymentModeCheckboxConfig?.valueType === true
          )
          ?.map((paymentMethod: PaymentModeConcessionConfigType) => {
            return {
              paymentMethod: paymentMethod?.valueType,
              discountText: discountedText ? discountedText : ''
            }
          })
      return previewConfig && Boolean(previewConfig?.length) ? previewConfig : []
    }
    return []
  }, [discountFormState])

  return (
    <PaymentPageProvider>
      <div className='flex flex-wrap w-full'>
        <div className='flex flex-col w-7/12 gap-4'>
          <DiscountJourneyLine
            isSwitchable={Boolean(isEditMode)}
            config={modifiedJourneyLineConfig}
            currentStep={selectedStep}
            setCurrentStep={handleSetCurrentStep}
            currentStepIndex={nextStepIndex - 1}
            applicationStage={applicationStage}
          />
          {typeof discountFormState !== 'undefined' ? (
            <DiscountCreationForm
              discountFormState={discountFormState}
              setFormState={setDiscountFormState}
              currentStep={selectedStep}
              generationType={generationType}
              isEditMode={Boolean(isEditMode)}
              journeyLineConfig={modifiedJourneyLineConfig}
              periodLength={periodLength}
              saveTriggered={saveTriggered}
              applicationStage={applicationStage}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
            />
          ) : (
            <></>
          )}
          {isEditMode && typeof discountFormState === 'undefined' ? (
            <>
              <Skeleton variant='rectangular' sx={{ borderRadius: '16px' }} width={'100%'} height={200} />
              <Skeleton variant='rectangular' sx={{ borderRadius: '16px' }} width={'100%'} height={200} />
              <Skeleton variant='rectangular' sx={{ borderRadius: '16px' }} width={'100%'} height={200} />
              <Skeleton variant='rectangular' sx={{ borderRadius: '16px' }} width={'100%'} height={200} />
            </>
          ) : (
            <></>
          )}

          {/* Buttons for saving, creating, to continue and to save-continue */}
          <div className='flex items-center justify-end w-full gap-4'>
            <Button
              variant={'text'}
              id={`c2p-discount-flow-cancel-button`}
              sx={{
                display: Boolean(currentJourneyStepConfig?.cancelButton) ? 'flex' : 'none'
              }}
              className={`items-center max-w-fit h-9`}
              onClick={async () => {
                router.push('/rto-suite')
              }}
            >
              {currentJourneyStepConfig?.cancelButton?.text}
              {currentJourneyStepConfig?.cancelButton?.iconComponent ? (
                getIconComponent(currentJourneyStepConfig?.cancelButton?.iconComponent)
              ) : (
                <></>
              )}
            </Button>
            <Button
              variant={'contained'}
              id={`discount-flow-save-and-continue-button`}
              sx={{
                display: Boolean(
                  currentJourneyStepConfig?.saveAndContinueButton &&
                    !(lastStepIndex === nextStepIndex - 1 && !isEditMode)
                )
                  ? 'flex'
                  : 'none'
              }}
              className={`items-center max-w-fit`}
              onClick={async () => {
                if (
                  isInvalidForm(
                    discountFormState,
                    setFormErrors,
                    handleAlerts,
                    isEditMode,
                    generationType,
                    applicationStage
                  )
                )
                  return
                selectedStep === 'preview' ? setSaveTriggered(true) : handleSave()
              }}
            >
              {currentJourneyStepConfig?.saveAndContinueButton?.iconComponent &&
              applicationStage !== 'POST_ORDER_PLACEMENT' ? (
                getIconComponent(currentJourneyStepConfig?.saveAndContinueButton?.iconComponent)
              ) : (
                <></>
              )}
              {currentJourneyStepConfig?.saveAndContinueButton?.text}
            </Button>
            <LoadingButton
              disabled={user?.isDemoUser}
              variant={applicationStage === 'POST_ORDER_PLACEMENT' ? 'contained' : 'outlined'}
              id={`discount-flow-save-button`}
              sx={{
                display: Boolean(
                  currentJourneyStepConfig?.saveButton && !(lastStepIndex === nextStepIndex - 1 && !isEditMode)
                )
                  ? 'flex'
                  : 'none'
              }}
              loading={isLoading}
              loadingPosition='end'
              className={`items-center max-w-fit`}
              onClick={async () => {
                if (
                  isInvalidForm(
                    discountFormState,
                    setFormErrors,
                    handleAlerts,
                    isEditMode,
                    generationType,
                    applicationStage
                  )
                )
                  return
                selectedStep === 'preview' ? setSaveTriggered(true) : handleSave()
              }}
            >
              {currentJourneyStepConfig?.saveButton?.iconComponent && applicationStage !== 'POST_ORDER_PLACEMENT' ? (
                getIconComponent(currentJourneyStepConfig?.saveButton?.iconComponent)
              ) : (
                <></>
              )}
              {currentJourneyStepConfig?.saveButton?.text}
            </LoadingButton>

            <Button
              variant={'contained'}
              id={`discount-flow-continue-button`}
              sx={{
                display: Boolean(currentJourneyStepConfig?.continueButton && !(lastStepIndex === nextStepIndex - 1))
                  ? 'flex'
                  : 'none'
              }}
              className={`items-center max-w-fit h-9`}
              onClick={async () => {
                if (
                  isInvalidForm(
                    discountFormState,
                    setFormErrors,
                    handleAlerts,
                    isEditMode,
                    generationType,
                    applicationStage
                  )
                )
                  return
                goToNextStep()
              }}
            >
              {currentJourneyStepConfig?.continueButton?.text}
              {currentJourneyStepConfig?.continueButton?.iconComponent ? (
                getIconComponent(currentJourneyStepConfig?.continueButton?.iconComponent)
              ) : (
                <></>
              )}
            </Button>

            {/* onClick={jobButtonText === "Import" ? handleSync : handleExport} 
                  size="medium"
                  loadingPosition="end"
                  loading={isLoading}
                  endIcon={<ArrowRight />}
                  variant="contained" 
                  color={jobButtonColor} 
                  sx={{ fontSize: 16 }} */}

            <LoadingButton
              id='discount-flow-create-button'
              disabled={user?.isDemoUser}
              variant={'contained'}
              sx={{
                display: Boolean(
                  currentJourneyStepConfig?.createButton &&
                    (lastStepIndex === nextStepIndex - 1 || applicationStage === 'POST_ORDER_PLACEMENT')
                )
                  ? 'flex'
                  : 'none',
                outline: 'none'
              }}
              className={`items-center max-w-fit
             ${
               (discountFormState?.setup?.applicationConfig?.offerValueConfig?.value === '' ||
                 (discountFormState?.setup?.applicationConfig?.offerValueConfig?.valueType === 'PERCENTAGE_CAPPED'
                   ? discountFormState?.setup?.applicationConfig?.offerValueConfig?.cappedValue === ''
                   : false)) &&
               'opacity-50 !cursor-not-allowed'
             }`}
              loading={isLoading}
              loadingPosition='end'
              onClick={async () => {
                if (
                  isInvalidForm(
                    discountFormState,
                    setFormErrors,
                    handleAlerts,
                    isEditMode,
                    generationType,
                    applicationStage
                  )
                ) {
                  setFormErrors({ ...formErrors, offerLimit: defaultErrorState, offerValue: defaultErrorState })
                  return
                }
                if (Boolean(generationType === 'BULK')) {
                  handleSave()
                  return
                }
                handleSave()
              }}
            >
              {createButton?.text}
              {lastStepIndex !== nextStepIndex - 1 ? getIconComponent('ArrowRight') : <></>}
            </LoadingButton>
          </div>
        </div>
        <div className='relative w-5/12 px-6'>
          <div className='sticky flex flex-col gap-3 top-24'>
            {applicationStage === 'PRE_PAYMENT_VALIDATION' && selectedStep === 'preview' ? (
              <div className='w-full flex justify-center '>
                <PaymentPagePreview isPaymentOfferPreview={true} config={{ paymentMethods: paymentMethodsMessages }} />
              </div>
            ) : (
              <>
                {applicationStage !== 'POST_ORDER_PLACEMENT' && (
                  <ProgressiveDiscountSummary
                    formState={discountFormState}
                    currentStep={selectedStep}
                    journeyLineConfig={modifiedJourneyLineConfig}
                    isEditMode={Boolean(isEditMode)}
                    currentStepIndex={nextStepIndex - 1}
                    periodLength={periodLength}
                    applicationStage={applicationStage}
                  />
                )}

                <DiscountsChannelsForm discountFormState={discountFormState} setFormState={setDiscountFormState} />
              </>
            )}
          </div>
        </div>

        {/* Alerts */}
        {openAlert.length ? (
          <Alerts
            open={Boolean(openAlert.length)}
            duration={2000}
            closeHandler={handleCloseAlert}
            severity={openAlert}
            message={alertMessage}
          />
        ) : (
          <></>
        )}
      </div>
    </PaymentPageProvider>
  )
}

export default DiscountCreationFlow
